import { useRef, useCallback, useMemo, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { COMPLIANCE_STUDIOS } from '@artem-lex/common-server/complianceStudio'
import {
  useDeleteUserStandard,
  useUserStandard,
  useUserStandardUploadUrl,
  useUpdateUserStandardFile,
  useUserStandardDownloadUrl,
  useRemoveUserStandardFile,
  uploadUserStandardPdf,
} from '@/shared/hooks/api/userStandards'
import {
  isPasswordProtectedFileError,
  isRightsProtectedFileError,
  isUploadTooLargeError,
} from '@/shared/lib/uploadGuardrails'
import useI18n from '@/shared/hooks/usei18n'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { DeleteStandardDialog } from '../components/DeleteStandardDialog'
import { StandardDetailBreadcrumb } from '../components/StandardDetailBreadcrumb'
import { StandardDetailPageHeader } from '../components/StandardDetailPageHeader'
import { StandardInformationForUseSection } from '../components/StandardInformationForUseSection'
import { StandardProjectRelationSection } from '../components/StandardProjectRelationSection'
import { StandardRiskReductionMeasuresSection } from '../components/StandardRiskReductionMeasuresSection'
import { StandardScopeSection } from '../components/StandardScopeSection'
import { StandardSignificantHazardsSection } from '../components/StandardSignificantHazardsSection'
import {
  getStandardDetailBackSource,
  resolveStandardDetailBackTo,
} from '../standardDetailBackNavigation'
import { userStandardHasPdf } from '../standardDetailSectionProps'

export function StandardDetail() {
  const { t } = useTranslation(['standardsLibrary', 'common'])
  const { shortDateFormatterUtc } = useI18n()
  const { standardId } = useParams<{ standardId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    data: standard,
    isLoading,
    isError,
  } = useUserStandard(standardId ?? null)
  const complianceStudios = COMPLIANCE_STUDIOS
  const uploadUrlMutation = useUserStandardUploadUrl(standardId ?? null)
  const { mutate: deleteUserStandard, isPending: isDeletingStandard } =
    useDeleteUserStandard()
  const updateFileMutation = useUpdateUserStandardFile(standardId ?? null)
  const downloadUrlMutation = useUserStandardDownloadUrl(standardId ?? null)
  const removeFileMutation = useRemoveUserStandardFile(standardId ?? null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pdfSectionRef = useRef<HTMLDivElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgressPercent, setUploadProgressPercent] = useState<
    number | null
  >(null)

  const linkedStudios = useMemo(() => {
    const ids = standard?.complianceStudioIds ?? []
    if (ids.length === 0) {
      return []
    }
    const studioMap = new Map(
      complianceStudios.map((studio) => [studio.id, studio])
    )
    return ids
      .map((id) => studioMap.get(id))
      .filter(Boolean) as typeof complianceStudios
  }, [standard?.complianceStudioIds, complianceStudios])

  const hasPdf = userStandardHasPdf(standard)

  const pdfUploadingLabel = useMemo(() => {
    if (!isUploading) {
      return ''
    }
    if (uploadProgressPercent != null) {
      return t('standardsLibrary:detail.pdfSection.uploadingWithPercent', {
        percent: uploadProgressPercent,
      })
    }
    return t('standardsLibrary:detail.pdfSection.uploading')
  }, [isUploading, uploadProgressPercent, t])

  const handleUploadPdf = useCallback(
    async (file: File) => {
      if (!standardId || !standard) {
        return
      }
      setIsUploading(true)
      setUploadProgressPercent(null)
      try {
        await uploadUserStandardPdf(standardId, file, {
          getUploadUrl: (key) => uploadUrlMutation.mutateAsync(key),
          updateFile: (data) => updateFileMutation.mutateAsync(data),
          onUploadProgress: (percent) => {
            setUploadProgressPercent(percent)
          },
        })
        toast.success(t('standardsLibrary:detail.pdfSection.uploadSuccess'))
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } catch (err) {
        let message = t('standardsLibrary:detail.pdfSection.uploadError')
        if (isUploadTooLargeError(err)) {
          message = t('standardsLibrary:detail.pdfSection.fileTooLarge')
        } else if (isPasswordProtectedFileError(err)) {
          message = t('standardsLibrary:detail.pdfSection.fileEncrypted')
        } else if (isRightsProtectedFileError(err)) {
          message = t('standardsLibrary:detail.pdfSection.fileRightsProtected')
        } else if (err instanceof Error && err.message !== 'Only PDF files are allowed') {
          message = err.message
        }
        toast.error(message)
      } finally {
        setIsUploading(false)
        setUploadProgressPercent(null)
      }
    },
    [standardId, standard, uploadUrlMutation, updateFileMutation, t]
  )

  const handleFileChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0]
      if (!file) {
        return
      }
      void handleUploadPdf(file)
      ev.target.value = ''
    },
    [handleUploadPdf]
  )

  const handleDownload = useCallback(() => {
    downloadUrlMutation.mutate()
  }, [downloadUrlMutation])

  const scrollToPdfUpload = useCallback(() => {
    pdfSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
    if (!hasPdf) {
      window.setTimeout(() => {
        fileInputRef.current?.click()
      }, 400)
    }
  }, [hasPdf])

  const handleRemovePdf = useCallback(() => {
    if (!standardId) {
      return
    }
    removeFileMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('standardsLibrary:detail.pdfSection.removePdfSuccess'))
      },
      onError: (err) => {
        toast.error(
          err?.message ?? t('standardsLibrary:detail.pdfSection.removePdfError')
        )
      },
    })
  }, [standardId, removeFileMutation, t])

  const handleDeleteStandard = useCallback(() => {
    if (!standardId) {
      return
    }
    deleteUserStandard(standardId, {
      onSuccess: () => {
        toast.success(t('standardsLibrary:deleteDialog.success'))
        setDeleteDialogOpen(false)
        navigate('/standards-library', { replace: true })
      },
      onError: (error) => {
        toast.error(error.message || t('standardsLibrary:deleteDialog.error'))
      },
    })
  }, [deleteUserStandard, standardId, navigate, t])

  const backSource = useMemo(
    () => getStandardDetailBackSource(searchParams),
    [searchParams]
  )

  const backTo = useMemo(
    () => resolveStandardDetailBackTo(searchParams),
    [searchParams]
  )

  const backButtonLabel = useMemo(() => {
    if (
      getStandardDetailBackSource(searchParams) ===
      'machinery-directive-standards'
    ) {
      return t('standardsLibrary:detail.backToComplianceStudio')
    }
    return t('standardsLibrary:detail.backToStandardsLibrary')
  }, [backSource, t])

  if (isLoading) {
    return (
      <>
        <Header fixed>
          <Search />
          <div className='ms-auto flex items-center space-x-4'>
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <div className='flex items-center justify-center py-12 text-muted-foreground'>
            Loading…
          </div>
        </Main>
      </>
    )
  }

  if (isError || !standard) {
    return (
      <>
        <Header fixed>
          <Search />
          <div className='ms-auto flex items-center space-x-4'>
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <StandardDetailBreadcrumb
            backSource={backSource}
            backTo={backTo}
            currentLabel={t('standardsLibrary:notFound')}
          />
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              {t('standardsLibrary:notFound')}
            </h2>
            <p className='text-muted-foreground'>
              {t('standardsLibrary:detail.pageDescription')}
            </p>
          </div>
          <Button variant='link' type='button' className='w-fit px-0' asChild>
            <Link to={backTo}>{backButtonLabel}</Link>
          </Button>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
        <div className='flex w-full flex-col gap-6'>
          <StandardDetailPageHeader
            backSource={backSource}
            backTo={backTo}
            reference={standard.reference}
            onDelete={() => setDeleteDialogOpen(true)}
          />

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8'>
            <aside className='flex flex-col gap-6 lg:col-span-1'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-base'>
                    {t('standardsLibrary:detail.metadataSectionTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className='space-y-4 text-sm'>
                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.reference')}
                      </dt>
                      <dd className='mt-1'>{standard.reference}</dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.title')}
                      </dt>
                      <dd className='mt-1'>{standard.title}</dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.type')}
                      </dt>
                      <dd className='mt-1'>{standard.type}</dd>
                    </div>

                    {!standard.inEffect && standard.withdrawalOjDate && (
                      <div>
                        <dt className='font-medium text-muted-foreground'>
                          {t('standardsLibrary:detail.withdrawalOjDate')}
                        </dt>
                        <dd className='mt-1'>
                          {shortDateFormatterUtc(
                            new Date(standard.withdrawalOjDate)
                          )}
                        </dd>
                      </div>
                    )}

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.status')}
                      </dt>
                      <dd className='mt-1'>
                        <Badge
                          variant='outline'
                          className={
                            standard.inEffect
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-red-200 bg-red-50 text-red-700'
                          }
                        >
                          {standard.inEffect
                            ? t('standardsLibrary:createNew.statusCurrent')
                            : t('standardsLibrary:createNew.statusWithdrawn')}
                        </Badge>
                      </dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.createdByUser')}
                      </dt>
                      <dd className='mt-1'>
                        {standard.isImportedFromStandards
                          ? t('standardsLibrary:createdByUserNo')
                          : t('standardsLibrary:createdByUserYes')}
                      </dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.createdAt')}
                      </dt>
                      <dd className='mt-1'>
                        {standard.createdAt
                          ? new Date(standard.createdAt).toLocaleString()
                          : '—'}
                      </dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.updatedAt')}
                      </dt>
                      <dd className='mt-1'>
                        {standard.updatedAt
                          ? new Date(standard.updatedAt).toLocaleString()
                          : '—'}
                      </dd>
                    </div>

                    <div>
                      <dt className='font-medium text-muted-foreground'>
                        {t('standardsLibrary:detail.complianceStudios')}
                      </dt>
                      <dd className='mt-1'>
                        {linkedStudios.length > 0 ? (
                          <div className='flex flex-wrap gap-1.5'>
                            {linkedStudios.map((studio) => (
                              <Badge key={studio.id} variant='secondary'>
                                {studio.title}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              <Card ref={pdfSectionRef} id='standard-detail-pdf-section'>
                <CardHeader>
                  <CardTitle>
                    {t('standardsLibrary:detail.pdfSection.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  {hasPdf ? (
                    <>
                      <p className='text-sm text-muted-foreground'>
                        {standard.fileName ??
                          t('standardsLibrary:detail.pdfSection.attachedPdf')}
                      </p>
                      <div className='flex flex-wrap gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={handleDownload}
                          disabled={downloadUrlMutation.isPending}
                        >
                          {downloadUrlMutation.isPending && (
                            <Loader2 className='mr-2 size-4 animate-spin' />
                          )}
                          {downloadUrlMutation.isPending
                            ? t(
                                'standardsLibrary:detail.pdfSection.downloading'
                              )
                            : t(
                                'standardsLibrary:detail.pdfSection.downloadPdf'
                              )}
                        </Button>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                        >
                          {isUploading && (
                            <Loader2 className='mr-2 size-4 animate-spin' />
                          )}
                          {!isUploading && (
                            <Upload className='mr-2 size-4' aria-hidden />
                          )}
                          {isUploading
                            ? pdfUploadingLabel
                            : t(
                                'standardsLibrary:detail.pdfSection.replacePdf'
                              )}
                        </Button>
                        <Button
                          type='button'
                          variant='destructive'
                          size='sm'
                          onClick={handleRemovePdf}
                          disabled={removeFileMutation.isPending}
                        >
                          {removeFileMutation.isPending && (
                            <Loader2 className='mr-2 size-4 animate-spin' />
                          )}
                          {removeFileMutation.isPending
                            ? t('standardsLibrary:detail.pdfSection.removing')
                            : t('standardsLibrary:detail.pdfSection.removePdf')}
                        </Button>
                      </div>
                      <input
                        ref={fileInputRef}
                        type='file'
                        accept='application/pdf,.pdf'
                        className='hidden'
                        onChange={handleFileChange}
                        aria-label={t(
                          'standardsLibrary:detail.pdfSection.replacePdf'
                        )}
                      />
                    </>
                  ) : (
                    <>
                      <p className='text-sm text-muted-foreground'>
                        {t('standardsLibrary:detail.pdfSection.noPdfUploaded')}
                      </p>
                      <div className='flex items-center gap-2'>
                        <Button
                          type='button'
                          size='sm'
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                        >
                          {isUploading && (
                            <Loader2 className='mr-2 size-4 animate-spin' />
                          )}
                          {!isUploading && (
                            <Upload className='mr-2 size-4' aria-hidden />
                          )}
                          {isUploading
                            ? pdfUploadingLabel
                            : t('standardsLibrary:detail.pdfSection.uploadPdf')}
                        </Button>
                        <input
                          ref={fileInputRef}
                          type='file'
                          accept='application/pdf,.pdf'
                          className='hidden'
                          onChange={handleFileChange}
                          aria-label={t(
                            'standardsLibrary:detail.pdfSection.uploadPdf'
                          )}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <StandardProjectRelationSection standard={standard} />
            </aside>

            <div className='flex min-w-0 flex-col gap-6 lg:col-span-2'>
              <StandardScopeSection
                standard={standard}
                hasPdf={hasPdf}
                isPdfUploading={isUploading}
                onUploadPdf={scrollToPdfUpload}
              />

              <StandardSignificantHazardsSection
                standard={standard}
                hasPdf={hasPdf}
                isPdfUploading={isUploading}
                onUploadPdf={scrollToPdfUpload}
              />

              <StandardRiskReductionMeasuresSection
                standard={standard}
                hasPdf={hasPdf}
                isPdfUploading={isUploading}
                onUploadPdf={scrollToPdfUpload}
              />

              <StandardInformationForUseSection
                standard={standard}
                hasPdf={hasPdf}
                isPdfUploading={isUploading}
                onUploadPdf={scrollToPdfUpload}
              />
            </div>
          </div>

          <DeleteStandardDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            standard={standard}
            isLoading={isDeletingStandard}
            onConfirm={handleDeleteStandard}
          />
        </div>
      </Main>
    </>
  )
}
